// O simulador na tela só APLICA a curva que o motor mediu (supabase/functions/_shared/simulador.ts):
// conversões = ritmo atual × (verba nova ÷ verba atual) ^ b. Nada é medido aqui.
// A faixa junta duas incertezas: a de b (a resposta à verba) e a do próprio ritmo atual
// (com poucas conversões no período, o ritmo é pouco preciso).

export type Elasticidade = { valor: number; baixa: number; alta: number; fonte: 'historico' | 'faixa_padrao'; motivo: string | null }
export type Modelo = {
  campanha_id: string
  campanha: string
  plataforma: string
  base: { investimento: number; conversoes: number; valor: number; dias: number; conversoesNoPeriodo: number }
  elasticidade: Elasticidade | null
  historico: { minimo: number; maximo: number }
  ativa: boolean
  demo: boolean
}

export type Projecao = {
  conversoes: number
  baixa: number
  alta: number
  valor: number
  /** O cenário sai do que a campanha já gastou (média de 7 dias), com uma folga. */
  foraDoHistorico: 'acima' | 'abaixo' | null
}

export const DIAS_MES = 30
/** Folga em volta do histórico antes de avisar que é extrapolação. */
export const FOLGA = 0.2

/** Conversões por dia com esta verba diária. */
export function projetar(m: Modelo, investimento: number): Projecao {
  const fora = investimento > m.historico.maximo * (1 + FOLGA) ? 'acima' : investimento > 0 && investimento < m.historico.minimo * (1 - FOLGA) ? 'abaixo' : null
  if (investimento <= 0 || !m.elasticidade || m.base.conversoes <= 0 || m.base.investimento <= 0) {
    return { conversoes: 0, baixa: 0, alta: 0, valor: 0, foraDoHistorico: investimento > 0 ? fora : null }
  }
  const r = investimento / m.base.investimento
  const e = m.elasticidade
  // Aumentar: o pessimista é o b baixo. Cortar: o pessimista é o b alto (perde mais).
  const [bPess, bOtim] = r >= 1 ? [e.baixa, e.alta] : [e.alta, e.baixa]
  const ruido = Math.min(1, 1.96 / Math.sqrt(Math.max(1, m.base.conversoesNoPeriodo)))
  const central = m.base.conversoes * r ** e.valor
  return {
    conversoes: central,
    baixa: m.base.conversoes * r ** bPess * (1 - ruido),
    alta: m.base.conversoes * r ** bOtim * (1 + ruido),
    valor: m.base.valor * r ** e.valor,
    foraDoHistorico: fora,
  }
}

/** Verba diária com que cada campanha começa no cenário: a de hoje (pausada = 0). */
export const verbaAtual = (m: Modelo) => (m.ativa ? m.base.investimento : 0)

export type Divisao = { verbas: Record<string, number>; sobra: number }

/**
 * Divide um total diário entre as campanhas para ter mais conversões (estimadas): cada real vai
 * para onde a curva diz que rende mais, sem sair do histórico de cada campanha. Ficam de fora,
 * com a verba de hoje, as que não dá para estimar (sem conversão) e as pausadas.
 */
export function dividir(modelos: Modelo[], total: number): Divisao {
  const verbas: Record<string, number> = {}
  const livres = modelos.filter((m) => m.ativa && m.elasticidade && m.base.conversoes > 0)
  const fixas = modelos.filter((m) => !livres.includes(m))
  for (const m of fixas) verbas[m.campanha_id] = verbaAtual(m)
  let resto = total - fixas.reduce((s, m) => s + verbas[m.campanha_id], 0)
  if (!livres.length || resto <= 0) {
    for (const m of livres) verbas[m.campanha_id] = 0
    return { verbas, sobra: Math.max(0, resto) }
  }

  const minimo = (m: Modelo) => m.historico.minimo * (1 - FOLGA)
  const maximo = (m: Modelo) => m.historico.maximo * (1 + FOLGA)
  const somaMin = livres.reduce((s, m) => s + minimo(m), 0)
  // Nem o mínimo de cada uma cabe: divide na proporção dos mínimos.
  if (somaMin >= resto) {
    for (const m of livres) verbas[m.campanha_id] = (minimo(m) / somaMin) * resto
    return { verbas, sobra: 0 }
  }
  for (const m of livres) verbas[m.campanha_id] = minimo(m)
  resto -= somaMin

  const passo = Math.max(1, resto / 500)
  while (resto > 1e-9) {
    const p = Math.min(passo, resto)
    let melhor: Modelo | null = null
    let ganho = -Infinity
    let quanto = 0
    for (const m of livres) {
      const v = verbas[m.campanha_id]
      // Perto do teto, o passo vai só até ele.
      const cabe = Math.min(p, maximo(m) - v)
      if (cabe <= 1e-9) continue
      const g = (projetar(m, v + cabe).conversoes - projetar(m, v).conversoes) / cabe
      if (g > ganho) {
        ganho = g
        melhor = m
        quanto = cabe
      }
    }
    if (!melhor) break
    verbas[melhor.campanha_id] += quanto
    resto -= quanto
  }
  return { verbas, sobra: Math.max(0, resto) }
}
